import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, ScoreRing, fmtTime } from "@/components/ui-kit";
import { CheckCircle2, XCircle, MinusCircle, ArrowLeft, Sparkles, TrendingUp, Target, ClipboardCheck, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RubricView } from "@/components/RubricInputs";
import { FeedbackPdfView } from "@/components/FeedbackPdf";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function MixedSaqCard({ att }) {
  const marked = !att.saq_pending && att.saq_per_question;
  const byId = Object.fromEntries((att.saq_per_question || []).map((p) => [p.qid, p]));
  return (
    <div className="ember-card mt-6 p-6" data-testid="mixed-saq-result">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Written answers</h2>
        {marked
          ? <span className="rounded-full bg-[#ECFDF5] px-3 py-1 text-sm font-bold text-[#059669]" data-testid="saq-score">{att.saq_score}/{att.saq_total} marks{att.overall_percentage != null ? ` · Overall ${att.overall_percentage}%` : ""}</span>
          : <span className="rounded-full bg-purple-50 px-3 py-1 text-sm font-bold text-purple-700" data-testid="saq-pending">Awaiting tutor marking</span>}
      </div>
      <div className="mt-4 space-y-3">
        {(att.saq_answers || []).map((s) => (
          <div key={s.qid} className="rounded-xl border border-[#EEEAF8] p-3" data-testid={`saq-result-${s.number}`}>
            <div className="flex justify-between text-sm font-bold text-[#1E1B2E]"><span>Q{s.number}{s.label && s.label !== `Q${s.number}` ? ` · ${s.label}` : ""}</span>
              <span className="text-[#948EAB]">{marked && byId[s.qid] ? `${byId[s.qid].awarded}/${s.max_marks}` : `${s.max_marks} marks`}</span></div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-[#524B6B]">{s.your_answer || "No answer"}</p>
            {marked && byId[s.qid]?.rubric && <RubricView r={byId[s.qid].rubric} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ResultView() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [att, setAtt] = useState(null);
  const [compare, setCompare] = useState(false);
  useEffect(() => { api.get(`/attempts/${attemptId}`).then((r) => setAtt(r.data)); }, [attemptId]);
  if (!att) return <Loading />;

  const hasSolution = !!att.solution_url;
  const reviewRows = (att.per_question && att.per_question.length)
    ? [...att.per_question.map((pq) => ({ key: pq.qid, label: `Q${pq.number}`, your: pq.your_answer || "—", correct: pq.correct_answer || "—", ok: pq.correct, mcq: true })),
      ...(att.saq_answers || []).map((s) => ({ key: s.qid, label: `Q${s.number}`, your: s.your_answer || "—", mcq: false }))]
    : (att.answers_review || []).map((r, i) => ({ key: i, label: r.label || `Q${r.number || i + 1}`, your: r.your_answer || "—", mcq: false }));
  const compareBtn = hasSolution ? (
    <button onClick={() => setCompare(true)} data-testid="btn-view-model-answers"
      className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white shadow-md shadow-amber-500/25 transition-colors hover:bg-amber-600">
      <FileText className="h-4 w-4" /> Compare with model answers
    </button>
  ) : null;
  const CompareModal = (
    <Dialog open={compare} onOpenChange={setCompare}>
      <DialogContent className="max-w-6xl overflow-hidden p-0" data-testid="solutions-compare-modal">
        <DialogHeader className="border-b border-slate-200 px-5 py-4"><DialogTitle className="font-heading">Compare with model answers</DialogTitle></DialogHeader>
        <div className="grid h-[72vh] grid-cols-1 md:grid-cols-2">
          <div className="overflow-y-auto border-b border-slate-200 p-5 md:border-b-0 md:border-r" data-testid="compare-your-answers">
            <div className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">Your answers</div>
            <div className="space-y-2">
              {reviewRows.length === 0 && <p className="text-sm text-slate-500">No answers recorded.</p>}
              {reviewRows.map((r) => (
                <div key={r.key} className={`rounded-xl border p-3 ${r.mcq ? (r.ok ? "border-emerald-200 bg-emerald-50" : (r.your === "—" ? "border-slate-200 bg-slate-50" : "border-rose-200 bg-rose-50")) : "border-slate-200 bg-slate-50"}`}>
                  <div className="text-xs font-bold text-slate-500">{r.label}</div>
                  {r.mcq ? (
                    <div className="mt-1 flex flex-wrap gap-x-4 text-sm">
                      <span className="font-semibold text-slate-900">You: {r.your}</span>
                      <span className="font-semibold text-emerald-700">Correct: {r.correct}</span>
                    </div>
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-900">{r.your}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="h-full bg-slate-100">
            {hasSolution ? (
              <iframe title="solutions" src={`${BACKEND}${att.solution_url}#view=FitH`} className="h-full w-full" data-testid="compare-solutions-pdf" />
            ) : (
              <div className="grid h-full place-items-center text-sm text-slate-500">No solutions uploaded.</div>
            )}
          </div>
        </div>
        {hasSolution && (
          <div className="flex justify-end border-t border-slate-200 px-5 py-3">
            <a href={`${BACKEND}${att.solution_url}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-amber-600 hover:underline">Open solutions in a new tab ↗</a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  if (att.being_marked) {
    const isSaq = att.format === "saq";
    return (
      <div className="animate-fade-up mx-auto max-w-lg py-10">
        <button onClick={() => navigate("/student")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
        <div className="ember-card p-10 text-center" data-testid="being-marked">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl ember-gradient-soft text-[#7C3AED]"><ClipboardCheck className="h-8 w-8" /></div>
          <h1 className="mt-5 font-heading text-2xl font-extrabold text-[#1E1B2E]">Submitted — tutor feedback will be provided</h1>
          <p className="mt-2 text-[#524B6B]">{att.title}</p>
          <p className="mt-4 text-sm leading-relaxed text-[#524B6B]">Nice work — your {isSaq ? "written answers have" : "response has"} been sent to your tutor, who will mark {isSaq ? "them" : "it"} and add personalised feedback. In the meantime, open the model answers below to check your work.</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-bold text-purple-700">Awaiting tutor feedback</div>
          {compareBtn && <div className="mt-6 flex justify-center">{compareBtn}</div>}
        </div>
        {CompareModal}
      </div>
    );
  }

  const fb = att.feedback || {};

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back</button>
      {compareBtn && <span className="mb-4 ml-3 inline-flex">{compareBtn}</span>}

      {/* Score hero */}
      <div className="ember-card overflow-hidden">
        <div className="ember-gradient-soft flex flex-wrap items-center gap-8 p-8">
          <ScoreRing pct={att.percentage} />
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-[#1E1B2E]">Homework Complete</h1>
            <p className="mt-1 text-[#524B6B]">{att.title}</p>
            <div className="mt-4 flex items-center gap-6">
              <div><div className="font-heading text-2xl font-extrabold text-[#1E1B2E]" data-testid="result-score">{att.score} / {att.total}</div><div className="text-xs font-semibold text-[#948EAB]">{att.format === "mixed" ? "MCQ correct" : "Correct answers"}</div></div>
              <div><div className="font-heading text-2xl font-extrabold text-[#1E1B2E]">{fmtTime(att.time_taken_seconds)}</div><div className="text-xs font-semibold text-[#948EAB]">Time taken</div></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 divide-x divide-[#F1EEFA]">
          <div className="p-5 text-center"><div className="mx-auto grid h-9 w-9 place-items-center rounded-lg bg-[#ECFDF5] text-[#059669]"><CheckCircle2 className="h-5 w-5" /></div><div className="mt-2 font-heading text-xl font-extrabold text-[#1E1B2E]">{att.correct}</div><div className="text-xs text-[#948EAB]">Correct</div></div>
          <div className="p-5 text-center"><div className="mx-auto grid h-9 w-9 place-items-center rounded-lg bg-[#FFF1F2] text-[#E11D48]"><XCircle className="h-5 w-5" /></div><div className="mt-2 font-heading text-xl font-extrabold text-[#1E1B2E]">{att.incorrect}</div><div className="text-xs text-[#948EAB]">Incorrect</div></div>
          <div className="p-5 text-center"><div className="mx-auto grid h-9 w-9 place-items-center rounded-lg bg-[#F3F4F6] text-[#6B7280]"><MinusCircle className="h-5 w-5" /></div><div className="mt-2 font-heading text-xl font-extrabold text-[#1E1B2E]">{att.unanswered}</div><div className="text-xs text-[#948EAB]">Unanswered</div></div>
        </div>
      </div>

      {att.format === "mixed" && <MixedSaqCard att={att} />}

      {att.feedback_pdf_url && <div className="mt-6"><FeedbackPdfView url={att.feedback_pdf_url} /></div>}

      {/* Feedback */}
      <div className="ember-card mt-6 p-6" data-testid="feedback-panel">
        <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg ember-gradient text-white"><Sparkles className="h-4 w-4" /></span><h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Your personalised feedback</h2></div>
        <p className="mt-3 text-[15px] leading-relaxed text-[#1E1B2E]">{fb.summary}</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-bold text-[#059669]"><TrendingUp className="h-4 w-4" /> Strengths</h3>
            <ul className="mt-2 space-y-1.5">{(fb.strengths || []).map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#524B6B]"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#059669]" /> {s}</li>)}</ul>
          </div>
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-bold text-[#E11D48]"><Target className="h-4 w-4" /> Areas to improve</h3>
            <ul className="mt-2 space-y-1.5">{(fb.improve || []).map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#524B6B]"><Target className="mt-0.5 h-4 w-4 shrink-0 text-[#E11D48]" /> {s}</li>)}</ul>
          </div>
        </div>
        {fb.recommendation && <div className="mt-5 rounded-xl bg-[#F5F3FF] p-4 text-sm text-[#5B21B6]"><b>Recommended practice:</b> {fb.recommendation}</div>}
      </div>

      {/* Skill breakdown */}
      {att.skill_breakdown && (
        <div className="ember-card mt-6 p-6">
          <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Performance by skill</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(att.skill_breakdown).map(([topic, v]) => {
              const acc = Math.round(100 * v.correct / (v.total || 1));
              const color = acc >= 80 ? "#059669" : acc >= 65 ? "#7C3AED" : "#E11D48";
              return (
                <div key={topic}>
                  <div className="mb-1 flex justify-between text-sm"><span className="font-semibold text-[#1E1B2E]">{topic}</span><span className="font-bold" style={{ color }}>{acc}% ({v.correct}/{v.total})</span></div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#EDE9FE]"><div className="h-full rounded-full" style={{ width: `${acc}%`, backgroundColor: color }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Question review */}
      <h2 className="mb-4 mt-8 flex items-center gap-2 font-heading text-xl font-extrabold text-[#1E1B2E]"><ClipboardCheck className="h-5 w-5 text-[#7C3AED]" /> Question review</h2>
      <div className="space-y-4">
        {att.per_question.map((pq) => (
          <div key={pq.qid} className="ember-card p-6" data-testid={`review-q-${pq.number}`}>
            <div className="flex items-center justify-between">
              <span className="grid h-8 min-w-8 place-items-center rounded-lg bg-[#EDE9FE] px-2 text-sm font-bold text-[#5B21B6]">Q{pq.number}</span>
              {pq.correct
                ? <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-3 py-1 text-xs font-bold text-[#059669]"><CheckCircle2 className="h-3.5 w-3.5" /> Correct</span>
                : pq.your_answer
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF1F2] px-3 py-1 text-xs font-bold text-[#E11D48]"><XCircle className="h-3.5 w-3.5" /> Incorrect</span>
                  : <span className="rounded-full bg-[#F3F4F6] px-3 py-1 text-xs font-bold text-[#6B7280]">Unanswered</span>}
            </div>
            <p className="mt-3 font-heading font-bold text-[#1E1B2E]">{pq.text || `Question ${pq.number}`}</p>
            {pq.image_url && <img src={pq.image_url} alt="diagram" className="mt-3 max-h-64 rounded-xl border border-[#E9E5F5]" />}
            {pq.text ? (
              <div className="mt-4 space-y-2">
                {pq.options.map((o) => {
                  const isCorrect = o.key.toLowerCase() === (pq.correct_answer || "").toLowerCase();
                  const isYours = o.key.toLowerCase() === (pq.your_answer || "").toLowerCase();
                  let cls = "border-[#E9E5F5]";
                  if (isCorrect) cls = "border-[#059669] bg-[#ECFDF5]";
                  else if (isYours) cls = "border-[#E11D48] bg-[#FFF1F2]";
                  return (
                    <div key={o.key} className={`flex items-center gap-3 rounded-xl border-2 p-3 ${cls}`}>
                      <span className="grid h-7 w-7 place-items-center rounded-lg border border-current text-xs font-bold text-[#524B6B]">{o.key}</span>
                      <span className="flex-1 text-sm text-[#1E1B2E]">{o.text}</span>
                      {isCorrect && <span className="text-xs font-bold text-[#059669]">Correct answer</span>}
                      {isYours && !isCorrect && <span className="text-xs font-bold text-[#E11D48]">Your answer</span>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap gap-3">
                <span className={`rounded-xl border-2 px-4 py-2 text-sm font-bold ${pq.correct ? "border-[#059669] bg-[#ECFDF5] text-[#059669]" : "border-[#E11D48] bg-[#FFF1F2] text-[#E11D48]"}`}>Your answer: {pq.your_answer || "—"}</span>
                <span className="rounded-xl border-2 border-[#059669] bg-[#ECFDF5] px-4 py-2 text-sm font-bold text-[#059669]">Correct answer: {pq.correct_answer || "—"}</span>
              </div>
            )}
            {pq.explanation && (
              <div className="mt-4 rounded-xl bg-[#F5F3FF] p-4">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#5B21B6]">Explanation {pq.ai_explanation && <span className="rounded bg-[#EDE9FE] px-1.5 py-0.5 text-[10px]">AI</span>}</div>
                <p className="mt-1 text-sm leading-relaxed text-[#1E1B2E]">{pq.explanation}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {CompareModal}
    </div>
  );
}
